/**
 * Alerts Option A megaphone: hold-to-talk live talk-down (ENG-1721 transport)
 * with Jess's mockup chrome (icon button + status banner under the tab row).
 */
import React from "react";
import {
  formatLiveElapsed,
  LiveStatusPollOptions,
  LiveTalkDownPhase,
  useLiveTalkDown,
} from "../../common/TalkDown/useLiveTalkDown";
import {
  TalkDownGate,
  useTalkDownEnabled,
  useTalkDownMode,
} from "../../common/TalkDown/useTalkDownEnabled";
import { useTalkDownLearnedIncapable } from "../../common/TalkDown/talkDownCapabilityStore";
import { WhipDeps } from "../../common/TalkDown/whipPublish";
import {
  TALK_DOWN_NEEDS_PRODUCTION_MODE_MESSAGE,
  TALK_DOWN_NEEDS_PRODUCTION_MODE_STAFF_MESSAGE,
} from "../../common/TalkDown/talkDownApi";
import { liveMediaNeedsProductionMode } from "../../utils/productionMode";
import { useIsStaffUser } from "../../utils/staffUser";

export const ALERT_LIVE_TALK_DOWN_LABEL = "Hold to talk down (live)";
export const ALERT_LIVE_TALK_DOWN_UNAVAILABLE_LABEL =
  "Talk-down isn't available for this camera";
export const ALERT_LIVE_TALK_DOWN_ACTIVE_TITLE = "Live talk down in progress";
export const ALERT_LIVE_TALK_DOWN_RELEASE_HINT = "Release the megaphone to end";
export const ALERT_LIVE_TALK_DOWN_DISMISS_LABEL = "Dismiss talk-down status";

const idleButtonClassName =
  "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border-[1.5px] border-[#cfd8e6] bg-white text-[#233762] disabled:cursor-default";
const unavailableButtonClassName =
  "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border-[1.5px] border-[#e2e8f2] bg-[#f5f7fa] text-[#9AA3B5] disabled:cursor-default";
const activeButtonClassName =
  "talk-down-pulse inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border-[1.5px] border-actuate-orange bg-actuate-orange text-white";
const pendingButtonClassName =
  "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border-[1.5px] border-[#9CA3AF] bg-[#9CA3AF] text-white disabled:cursor-default";

const MegaphoneIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m3 11 18-5v12L3 14v-3z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </svg>
);

/** Megaphone with a slash — used when talk-down isn't available for the camera. */
const MegaphoneUnavailableIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m3 11 18-5v12L3 14v-3z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    <line x1="3" y1="3" x2="21" y2="21" />
  </svg>
);

const MicIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#e57600"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" x2="12" y1="19" y2="22" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const WAVEFORM_HEIGHTS = [6, 12, 18, 10, 16, 8, 14, 11, 17, 9, 13, 7];

const LiveWaveform = () => (
  <div className="ml-1 flex h-5 items-end gap-[3px]" aria-hidden="true">
    {WAVEFORM_HEIGHTS.map((height, index) => (
      <span
        key={index}
        className="inline-block w-[3px] rounded-sm bg-actuate-orange"
        style={{ height }}
      />
    ))}
  </div>
);

const isActivationKey = (key: string) => key === " " || key === "Enter";

const TalkDownPulseStyles = () => (
  <style>{`
    @keyframes talk-down-pulse {
      0% { box-shadow: 0 0 0 0 rgba(255, 132, 0, 0.45); }
      100% { box-shadow: 0 0 0 12px rgba(255, 132, 0, 0); }
    }
    .talk-down-pulse {
      animation: talk-down-pulse 1.2s ease-out infinite;
    }
    @keyframes talk-down-dot {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.35; }
    }
    .talk-down-dot {
      animation: talk-down-dot 1s ease-in-out infinite;
    }
  `}</style>
);

export interface AlertTalkDownLiveStatus {
  phase: LiveTalkDownPhase;
  message: string;
  elapsedSeconds: number;
  /** Technical cause; the banner shows it to staff only. */
  detail?: string;
}

export interface AlertTalkDownLiveControlProps extends TalkDownGate {
  cameraId: string | number | null | undefined;
  cameraName: string;
  liveOptions?: {
    statusPoll?: LiveStatusPollOptions;
    idleMs?: number;
    whipDeps?: WhipDeps;
  };
  /** Called whenever the live status changes so the parent can render the banner. */
  onStatusChange?: (status: AlertTalkDownLiveStatus | null) => void;
  /**
   * Registers (or clears) a function that ends the open live session — used by
   * the banner dismiss X so closing the Ready banner also tears down the session.
   */
  onRegisterEndSession?: (endSession: (() => void) | null) => void;
}

export const AlertTalkDownLiveBanner = ({
  status,
  onDismiss,
}: {
  status: AlertTalkDownLiveStatus;
  /** Dismiss the banner and end any open session. Hidden while connecting or live. */
  onDismiss?: () => void;
}) => {
  const { phase, message, elapsedSeconds, detail } = status;
  const staff = useIsStaffUser();
  const isLive = phase === "live";
  const isConnecting = phase === "connecting";
  const isError = phase === "error" || phase === "failed";
  const isOk = phase === "ended";
  const canDismiss = Boolean(onDismiss) && !isLive && !isConnecting;

  const title = isLive
    ? ALERT_LIVE_TALK_DOWN_ACTIVE_TITLE
    : isConnecting
    ? "Connecting..."
    : message || "Talk-down";

  return (
    <div
      role="status"
      data-testid="alert-talk-down-live-banner"
      className={`mb-3 flex items-center gap-3 rounded-[10px] border-[1.5px] px-4 py-3 font-mulish text-[#20283a] ${
        isError
          ? "border-[#B3261E] bg-[#FCE8E6]"
          : isOk
          ? "border-[#1B7F3B] bg-[#E8F5EC]"
          : "border-actuate-orange bg-[#fff4e8]"
      }`}
    >
      <TalkDownPulseStyles />
      <span
        className={`talk-down-dot inline-block h-2.5 w-2.5 shrink-0 rounded-full ${
          isError ? "bg-[#B3261E]" : isOk ? "bg-[#1B7F3B]" : "bg-[#d64545]"
        }`}
      />
      {!isError && !isOk && <MicIcon />}
      <span className="text-[15px] font-semibold">{title}</span>
      {isLive && (
        <>
          <span className="font-mono text-[14px] text-[#54595f]">
            {formatLiveElapsed(elapsedSeconds)}
          </span>
          <LiveWaveform />
          <span className="ml-auto text-[13px] text-[#54595f]">
            {ALERT_LIVE_TALK_DOWN_RELEASE_HINT}
          </span>
        </>
      )}
      {!isLive && message && title !== message && (
        <span className="text-[13px] text-[#54595f]">{message}</span>
      )}
      {staff && detail && (isError || isOk) && (
        <span
          data-testid="alert-talk-down-live-detail"
          className="break-all font-mono text-[11px] text-[#7a8090]"
        >
          {detail}
        </span>
      )}
      {canDismiss && (
        <button
          type="button"
          aria-label={ALERT_LIVE_TALK_DOWN_DISMISS_LABEL}
          title={ALERT_LIVE_TALK_DOWN_DISMISS_LABEL}
          onClick={onDismiss}
          className="ml-auto -mr-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[#54595f] hover:bg-black/5 hover:text-[#20283a]"
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
};

const AlertTalkDownMegaphoneStub = ({
  label = ALERT_LIVE_TALK_DOWN_UNAVAILABLE_LABEL,
}: {
  label?: string;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    className={unavailableButtonClassName}
    disabled
  >
    <MegaphoneUnavailableIcon />
  </button>
);

const AlertTalkDownMegaphoneLive = ({
  cameraId,
  cameraName,
  liveOptions,
  onStatusChange,
  onRegisterEndSession,
}: {
  cameraId: string | number;
  cameraName: string;
  liveOptions?: AlertTalkDownLiveControlProps["liveOptions"];
  onStatusChange?: (status: AlertTalkDownLiveStatus | null) => void;
  onRegisterEndSession?: (endSession: (() => void) | null) => void;
}) => {
  const live = useLiveTalkDown({
    cameraId,
    cameraName,
    statusPoll: liveOptions?.statusPoll,
    idleMs: liveOptions?.idleMs,
    whipDeps: liveOptions?.whipDeps,
  });
  const { state, elapsedSeconds, busy, press, release, reset } = live;
  const heldKeyRef = React.useRef(false);

  React.useEffect(() => {
    if (state.phase === "idle") {
      onStatusChange?.(null);
      return;
    }
    onStatusChange?.({
      phase: state.phase,
      message: state.message,
      elapsedSeconds,
      ...(state.detail ? { detail: state.detail } : {}),
    });
  }, [
    state.phase,
    state.message,
    state.detail,
    elapsedSeconds,
    onStatusChange,
  ]);

  React.useEffect(() => {
    onRegisterEndSession?.(reset);
    return () => onRegisterEndSession?.(null);
  }, [reset, onRegisterEndSession]);

  React.useEffect(() => {
    return () => onStatusChange?.(null);
  }, [onStatusChange]);

  const isLive = state.phase === "live";
  const isConnecting = state.phase === "connecting";
  const holding = isLive || isConnecting;

  const buttonClassName = isLive
    ? activeButtonClassName
    : isConnecting || busy
    ? pendingButtonClassName
    : idleButtonClassName;

  return (
    <>
      <TalkDownPulseStyles />
      <button
        type="button"
        aria-label={ALERT_LIVE_TALK_DOWN_LABEL}
        aria-pressed={isLive}
        title={
          isLive
            ? ALERT_LIVE_TALK_DOWN_RELEASE_HINT
            : isConnecting
            ? "Connecting..."
            : state.phase === "closing"
            ? "Still closing the last session…"
            : ALERT_LIVE_TALK_DOWN_LABEL
        }
        disabled={busy}
        onPointerDown={(event) => {
          if (event.button !== undefined && event.button !== 0) return;
          event.preventDefault();
          press();
        }}
        onPointerUp={release}
        onPointerLeave={() => {
          if (holding) release();
        }}
        onPointerCancel={release}
        onKeyDown={(event) => {
          if (!isActivationKey(event.key) || event.repeat) return;
          event.preventDefault();
          heldKeyRef.current = true;
          press();
        }}
        onKeyUp={(event) => {
          if (!isActivationKey(event.key) || !heldKeyRef.current) return;
          heldKeyRef.current = false;
          release();
        }}
        onBlur={() => {
          if (holding) release();
        }}
        onContextMenu={(event) => event.preventDefault()}
        style={{ touchAction: "none", userSelect: "none" }}
        className={buttonClassName}
      >
        <MegaphoneIcon />
      </button>
    </>
  );
};

/**
 * Megaphone for the Alerts talk-down cluster. Wired to live push-to-talk when
 * showTalkDown + showLiveTalkDown + talk_down permission + RTSP/ONVIF site
 * + camera not known-incapable; otherwise a crossed-out stub.
 */
export const AlertTalkDownMegaphone = ({
  cameraId,
  cameraName,
  integrationTypeName,
  skipSiteTypeGate,
  talkDownCapable,
  liveOptions,
  onStatusChange,
  onRegisterEndSession,
}: AlertTalkDownLiveControlProps) => {
  const enabled = useTalkDownEnabled({
    integrationTypeName,
    skipSiteTypeGate,
    talkDownCapable,
  });
  const mode = useTalkDownMode();
  const learnedIncapable = useTalkDownLearnedIncapable(cameraId);
  const staff = useIsStaffUser();
  // dev/redesign on staging with Use production off: no real cameras, so a
  // start can only fail. Disabled, with the reason, and no transport.
  const needsProductionMode = liveMediaNeedsProductionMode();
  const canLive =
    enabled &&
    !learnedIncapable &&
    !needsProductionMode &&
    mode === "live" &&
    cameraId != null &&
    cameraId !== "";

  React.useEffect(() => {
    if (!canLive) {
      onStatusChange?.(null);
      onRegisterEndSession?.(null);
    }
  }, [canLive, onStatusChange, onRegisterEndSession]);

  if (!canLive) {
    const label =
      needsProductionMode && enabled && !learnedIncapable
        ? staff
          ? TALK_DOWN_NEEDS_PRODUCTION_MODE_STAFF_MESSAGE
          : TALK_DOWN_NEEDS_PRODUCTION_MODE_MESSAGE
        : undefined;
    return <AlertTalkDownMegaphoneStub label={label} />;
  }

  return (
    <AlertTalkDownMegaphoneLive
      key={`alert-live-${cameraId}`}
      cameraId={cameraId}
      cameraName={cameraName}
      liveOptions={liveOptions}
      onStatusChange={onStatusChange}
      onRegisterEndSession={onRegisterEndSession}
    />
  );
};
